/**
 * 0.44.11 - The site's navigation menu row, both levels, ONE component
 * for the public header and the admin's preview, so the preview cannot
 * differ from the site.
 *
 * items: [{id, label, href, external, new_tab, columns?: [{id, heading,
 *         items: [{id, label, href, external, new_tab}]}]}]
 * An item with columns is a button with a chevron that opens its panel
 * (the mega-menu's second level) on click; Esc, a click outside, or
 * choosing an option closes it. The panel opens under its item, left
 * aligned, moved left when it would pass the window's right edge.
 *
 * preview: links do not navigate; a click marks the item (or the item
 * holding the option) as the current page, via activeId / onActivate.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { resolveTitleText, useLibraryCacheRefresh } from '../lib/resolveTitleText';
import styles from './NavMenu.module.css';

/* 0.44.12 - The step navigator's triangle (HenaketStepper's CARET):
 * a filled 10x5 triangle pointing down, in the text colour, the same
 * whether the panel is open or not. */
const CARET = 'M0 0h10L5 5z';
function Chevron() {
  return (
    <svg className={styles.caret} viewBox="0 0 10 5" aria-hidden="true">
      <path d={CARET} />
    </svg>
  );
}

function Link({ it, language, className, preview, onChoose, role }) {
  const tabProps = it.new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  const text = resolveTitleText(it.label, language);
  if (preview) {
    return (
      <a href="#preview" className={typeof className === 'function' ? className({ isActive: false }) : className}
         role={role} onClick={(e) => { e.preventDefault(); onChoose && onChoose(); }}>{text}</a>
    );
  }
  if (it.external) {
    return (
      <a href={it.href} role={role} {...tabProps} onClick={onChoose}
         className={typeof className === 'function' ? className({ isActive: false }) : className}>{text}</a>
    );
  }
  return (
    // 0.44.22 - `end` for the home page (href ''): otherwise it would be
    // "current" on every page under /<lang>/.
    <NavLink to={`/${language}/${it.href}`} end={!it.href || String(it.href).startsWith('#')} role={role} {...tabProps} onClick={onChoose} className={className}>
      {text}
    </NavLink>
  );
}

function SubMenu({ item, language, linkClass, activeClass, preview, isActive, onActivate }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const [place, setPlace] = useState(null);   // { left, maxWidth } or null

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  /* 0.44.14 - Placement, within the page's content area (the header's
   * container minus its padding: the 12-column, 1168px area):
   *   1. centred under the item, when that fits the content area;
   *   2. otherwise aligned to the content area's left edge for an item in
   *      the left half of the area, its right edge for one in the right;
   *   3. a panel wider than the area starts at its left edge, capped.
   * Measured while invisible (no jump) and again on resize. */
  useLayoutEffect(() => {
    if (!open) { setPlace(null); return undefined; }
    const measure = () => {
      const wrap = wrapRef.current;
      const panel = panelRef.current;
      if (!wrap || !panel) return;
      const nav = wrap.closest('nav');
      const box = (nav && nav.parentElement) || document.documentElement;
      const r = box.getBoundingClientRect();
      const cs = window.getComputedStyle(box);
      const L = r.left + (parseFloat(cs.paddingLeft) || 0);
      const R = r.right - (parseFloat(cs.paddingRight) || 0);
      const W = R - L;
      const item = wrap.getBoundingClientRect();
      const w = Math.min(panel.scrollWidth, W);
      const centre = item.left + item.width / 2;
      let x;
      if (panel.scrollWidth >= W) x = L;                       // 3
      else if (centre - w / 2 >= L && centre + w / 2 <= R) x = centre - w / 2;   // 1
      else x = centre < L + W / 2 ? L : R - w;                // 2
      setPlace({ left: Math.round(x - item.left), maxWidth: Math.floor(W) });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open]);

  const cls = [linkClass, styles.trigger, (open || isActive) ? activeClass : ''].filter(Boolean).join(' ');
  return (
    <div className={styles.item} ref={wrapRef}>
      <button type="button" className={cls} aria-expanded={open} aria-haspopup="true"
              onClick={() => setOpen(o => !o)}>
        {resolveTitleText(item.label, language)}
        <Chevron />
      </button>
      {open && (
        <div className={styles.panel} ref={panelRef}
             style={place ? { left: place.left, maxWidth: place.maxWidth } : { visibility: 'hidden' }}>
          {item.columns.map(col => (
            <div key={col.id} className={styles.column}>
              {col.heading && <div className={styles.heading}>{resolveTitleText(col.heading, language)}</div>}
              {col.items.map(sub => (
                <Link key={sub.id} it={sub} language={language} className={styles.option} preview={preview}
                      onChoose={() => { setOpen(false); onActivate && onActivate(item.id); }} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* 0.44.16 - Small screens (the header's hamburger menu): the same panel
 * (frame, padding, shadow, heading and option styles, hover and focus),
 * opened under its item by tapping it (the same triangle), in the
 * menu's flow, with its columns stacked. */
export function MobileSubMenu({ item, language, rowClass, preview = false }) {
  // 0.45.55 - Library labels fill in when their library arrives.
  useLibraryCacheRefresh();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" className={`${rowClass} ${styles.trigger} ${styles.mobileTrigger}`}
              aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {resolveTitleText(item.label, language)}
        <Chevron />
      </button>
      {/* 0.44.18 - Inline in the framed dropdown, no panel of their own. */}
      {open && (
        <div className={styles.mobileSub}>
          {item.columns.map(col => (
            <div key={col.id} className={`${styles.column} ${styles.columnMobile}`}>
              {col.heading && <div className={styles.heading}>{resolveTitleText(col.heading, language)}</div>}
              {col.items.map(sub => (
                <Link key={sub.id} it={sub} language={language} className={styles.option} role="menuitem"
                      preview={preview} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NavMenu({ items, language, linkClass, activeClass, preview = false, activeId, onActivate }) {
  // 0.45.55 - Library labels fill in when their library arrives.
  useLibraryCacheRefresh();
  return (
    <>
      {(items || []).map(it => (it.columns && it.columns.length ? (
        <SubMenu key={it.id} item={it} language={language} linkClass={linkClass} activeClass={activeClass}
                 preview={preview} isActive={preview && activeId === it.id} onActivate={onActivate} />
      ) : (
        <Link key={it.id} it={it} language={language} preview={preview}
              onChoose={() => onActivate && onActivate(it.id)}
              className={preview
                ? (activeId === it.id ? `${linkClass} ${activeClass}` : linkClass)
                : ({ isActive }) => (isActive ? `${linkClass} ${activeClass}` : linkClass)} />
      )))}
    </>
  );
}
